import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  signal,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import {
  DialogCloseOptions,
  DialogCloseReason,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';
import { NomObjectTypes } from 'src/app/core/constants';
import { Feed } from 'src/app/core/models/core.models';
import { DimensionAttachParams } from 'src/app/core/services/feed.service';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { AppState } from 'src/app/core/state/app.state';
import { TabDialogBase } from 'src/app/shared/abstract/tab-dialog-base';
import { SearchableDialog, TabConfig } from 'src/app/shared/utils';
import { QueryUsedByComponent } from './query-used-by/query-used-by.component';
import { OutputLogsComponent } from '../output-logs/output-logs.component';
import { AuditTrailComponent } from '../audit-trail/audit-trail.component';
import { QueryEditorGeneralSettingsComponent } from '../query-editor-general-settings/query-editor-general-settings.component';
import { SearchBuilderComponent } from '../search-builder/search-builder.component';
import { ObjectPermissionsComponent } from '../../../objects/components/object-permissions/object-permissions.component';
import { Observable, take, tap } from 'rxjs';
import { selectUser } from 'src/app/core/state';
import { faFileCirclePlus, faGear } from '@fortawesome/free-solid-svg-icons';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import {
  createFeedSuccess,
  editFeedSuccess,
  feedAttachDimensionsSuccess,
} from '../../../core/state/feed.actions';
import { UtilsService } from 'src/app/core/services/utils.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { Actions, ofType } from '@ngrx/effects';
import { submitFormFailure } from 'src/app/core/state/form.actions';
import { QueryHintOverlayService } from '@services/query-hint-overlay.service';
import { FeedPersistenceService } from 'src/app/core/services/feed-persistence.service';
import { FeedDimensionsService } from 'src/app/core/services/feed-dimensions.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../search-input/search-input.component';
import { DialogTabNavComponent } from '../dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData {
  feed: Feed;
  query: string;
  objectTypeId: NomObjectTypes;
  dimensionAttachParams: DimensionAttachParams;
  isUpdateMode: boolean;
  tabId?: string;
}

interface DialogResult {}

@Component({
  selector: 'app-query-editor-dialog',
  templateUrl: './query-editor-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './query-editor-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class QueryEditorDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private utilsService = inject(UtilsService);
  private formService = inject(FormsService);
  private queryHintsOverlayService = inject(QueryHintOverlayService);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  private actions$ = inject(Actions);
  private feedPersistence = inject(FeedPersistenceService);
  private feedDimensions = inject(FeedDimensionsService);

  isSaving: boolean = false;

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  // A signal: the tab manager's form stream emits from a signal effect, after
  // the template in the same pass (WAP-1954).
  readonly objectTitle = signal('');
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  get isUpdateMode(): boolean {
    return !!this.feed;
  }

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get feed() {
    return this.dialogData.feed;
  }

  get query() {
    return this.dialogData.query ?? '';
  }

  set objectTypeId(value) {
    this.dialogData.objectTypeId = value;
  }

  get objectTypeId() {
    return this.dialogData.objectTypeId;
  }

  get dimensionAttachParams() {
    return this.dialogData.dimensionAttachParams;
  }

  get tabId() {
    return this.dialogData.tabId;
  }

  ngOnInit(): void {
    this.queryHintsOverlayService.forceClose();

    if (this.feed) {
      this.objectTypeId = this.feed.object_type_id;
    }

    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);

    if (this.tabId) {
      const exist = this.tabs.some((tab) => tab.id === this.tabId);
      if (!exist) return;

      this.tabManager.selectTab(this.tabId);
    }

    // ! update objectTitle dynamically
    this.tabManager.activeFormValueChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (this.isUpdateMode) {
          this.objectTitle.set(formData?.title ?? this.objectTitle());
        } else {
          this.objectTitle.set(formData?.title || this.objectTitleDefault);
        }
      });

    this.usedByRowCountSubscription();
  }

  usedByRowCountSubscription() {
    const usedByTab = this.tabs.find((tab) => tab.id === 'used-by');
    if (!usedByTab) return;

    const component = this.tabManager.getTabComponent(usedByTab.id) as {
      rowCountChanged: Observable<number>;
    };

    component.rowCountChanged
      .pipe(
        tap((count) => this.updateUsedByTabCount(count)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupUIState(): void {
    this.objectActionIcon = this.isUpdateMode ? faGear : faFileCirclePlus;
    this.objectIcon = this.isUpdateMode
      ? getIconForDataObject(
          this.feed.object_class_id,
          this.feed.object_type_id,
        )
      : faGear;

    this.objectActionTitle = this.isUpdateMode
      ? this.feed.object_type_id === NomObjectTypes.SEARCH_DIMENSION
        ? this.translateService.instant(
            'popup.action-title.edit-dimension-settings',
          )
        : this.translateService.instant('popup.action-title.edit-feed-settings')
      : this.translateService.instant(
          'popup.action-title.create-new-dimension',
        );
    this.objectTitle.set(this.feed?.title ?? '');
  }

  private setupTabs(): void {
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant('query-editor-popup.tab.general'),
        component: QueryEditorGeneralSettingsComponent,
        inputs: {
          searchString$: this.searchString$,
          objectTypeId: this.objectTypeId,
          feed: this.feed,
        },
      },
    ];

    const queryEditTab = (objectTypeId: NomObjectTypes, textKey: string) => ({
      id: 'search-builder',
      label: this.translateService.instant(textKey),
      component: SearchBuilderComponent,
      inputs: {
        searchString$: this.searchString$,
        objectTypeId,
        feed: this.feed,
        query: this.query,
        showPreviewPane: true,
      },
    });

    const combinedFeedTab = () => ({
      id: 'combine-feeds',
      label: this.translateService.instant(
        'query-editor-popup.tab.select-feeds-to-combine',
      ),
      component: SearchBuilderComponent,
      inputs: {
        searchString$: this.searchString$,
        objectTypeId: this.objectTypeId,
        feed: this.feed,
        showPreviewPane: true,
      },
    });

    switch (this.objectTypeId) {
      case NomObjectTypes.ONLINE_FEED:
      case NomObjectTypes.STORY_FEED:
      case NomObjectTypes.SOCIAL_FEED:
      case NomObjectTypes.BROADCAST_FEED:
      case NomObjectTypes.SEARCH_DIMENSION:
        this.tabs.push(
          queryEditTab(this.objectTypeId, 'query-editor-popup.tab.edit-query'),
        );
        break;
      case NomObjectTypes.COMBINED_FEED:
        this.tabs.push(combinedFeedTab());
        break;
    }

    if (this.feed) {
      this.tabs.push(
        {
          id: 'permissions',
          label: this.translateService.instant(
            'query-editor-popup.tab.permissions',
          ),
          component: ObjectPermissionsComponent,
          inputs: {
            searchString$: this.searchString$,
            objects: [this.feed],
            companyId: this.feed.company_id,
            allowOwnerChange: true,
          },
          objectPermissions: ['can_manage'],
        },
        {
          id: 'audit-trail',
          label: this.translateService.instant(
            'query-editor-popup.tab.audit-trail',
          ),
          component: AuditTrailComponent,
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          inputs: {
            searchString$: this.searchString$,
            object: this.feed,
            hasOwner: true,
            timeCreatedKey: 'time_created',
            timeModifiedKey: 'time_modified',
          },
          objectPermissions: ['can_manage'],
        },
      );
      if (this.feed.object_type_id !== NomObjectTypes.SEARCH_DIMENSION) {
        this.tabs.push({
          id: 'output-logs',
          label: this.translateService.instant(
            'query-editor-popup.tab.output-logs',
          ),
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          component: OutputLogsComponent,
          inputs: {
            object: this.feed,
          },
        });
      }
      this.tabs.push({
        id: 'used-by',
        label: this.translateService.instant('query-editor-popup.tab.used-by'),
        component: QueryUsedByComponent,
        inputs: {
          object: this.feed,
        },
      });
    }

    this.store
      .select(selectUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((user) => this.filterTabsByObjectPermissions(this.feed, user)),
      )
      .subscribe();
  }

  updateUsedByTabCount(count: number) {
    this.tabs = this.tabs.map((tab) =>
      tab.id === 'used-by' ? { ...tab, count } : tab,
    );
  }

  createFeed(formData: any) {
    let allFormData = this.utilsService.convertFormDataI18nKeys(formData);
    allFormData = {
      ...allFormData,
      object_type_id: this.objectTypeId,
      dimension_attach_params: this.dimensionAttachParams,
      query: allFormData['query_text'],
    };

    // ! only add dimension_attach_params if they exist
    if (this.dimensionAttachParams) {
      allFormData['dimension_attach_params'] = this.dimensionAttachParams;
    }

    // If dimension_attach_params exist, use the two-step create and attach flow
    if (this.dimensionAttachParams) {
      this.feedDimensions.createAndAttach(
        allFormData,
        this.dimensionAttachParams,
      );
    } else {
      // Regular feed/dimension creation
      this.feedPersistence.create(allFormData);
    }
  }

  updateFeed(formData: any) {
    let allFormData = this.utilsService.convertFormDataI18nKeys(formData);

    if (this.feed.object_type_id == NomObjectTypes.SOCIAL_FEED) {
      let socialSources = this.formService.extractFiltersFormDataPropertyValues(
        allFormData,
        'post_social_source_id',
      );

      // ! If extractFiltersFormDataPropertyValues didn't find anything,
      // ! check if the data is stored directly as post_social_source_id
      if (socialSources.length === 0 && allFormData['post_social_source_id']) {
        socialSources = Array.isArray(allFormData['post_social_source_id'])
          ? allFormData['post_social_source_id']
          : [allFormData['post_social_source_id']];
      }

      // ! Check if form data contains social source data at all
      const hasSocialSourceData =
        Object.keys(allFormData).some((key) =>
          key.includes('filters[post_social_source_id][include]'),
        ) || allFormData.hasOwnProperty('post_social_source_id');

      // ! If no social source data found in form, use existing feed data
      // ! But if data exists (even if empty), respect user's choice
      if (!hasSocialSourceData) {
        socialSources =
          this.feed.social_sources ||
          (this.feed as any).params?.social_sources ||
          [];
      }

      allFormData = {
        ...allFormData,
        params: {
          social_sources: socialSources,
        },
      };

      // * api has strange logic for some properties for buiudling query, this shoud not be sent
      this.formService.removePropertyByKey(
        allFormData,
        'post_social_source_id',
      );
    }

    if (typeof allFormData['workspace_id'] === 'undefined') {
      allFormData['workspace_id'] = this.feed.workspace_id;
    }
    if (typeof allFormData['title'] === 'undefined') {
      allFormData['title'] = this.feed.title;
    }

    this.feedPersistence.edit(this.feed.id, {
      ...allFormData,
      object_type_id: this.feed.object_type_id,
    });
  }

  protected override performSave(
    formData: any,
  ): DialogCloseOptions<DialogResult> {
    if (this.isUpdateMode) {
      this.updateFeed(formData);
      return { reason: DialogCloseReason.Update, data: formData };
    } else {
      this.createFeed(formData);
      return { reason: DialogCloseReason.Save, data: formData };
    }
  }

  save(): void {
    if (this.isSaving) return;

    this.isSaving = true;

    const formData = this.tabManager.combineValues();
    const result = this.performSave(formData);

    this.actions$
      .pipe(
        ofType(
          editFeedSuccess,
          createFeedSuccess,
          feedAttachDimensionsSuccess,
          submitFormFailure,
        ),
        take(1),
        tap((action) => {
          this.isSaving = false;

          if (
            action.type === editFeedSuccess.type ||
            action.type === createFeedSuccess.type ||
            action.type === feedAttachDimensionsSuccess.type
          ) {
            this.dirtyHandler.closeImmediately(result);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
